import React, { useState } from "react";
import "./index.css";
import moment from "moment";
import { Link } from "react-router-dom";

/* Components */
import TextRow from "./components/TextRow";
import ApplyJobModal from "../ApplyJobModal";

/* Helpers */
import { handleYachtType, handleYachtSize, handleJobSalary, handleContractType, handleStartDate, compareArchived, compareExpired } from "../../helpers/job";

/* API requests */
import { getApplications, updateApplication } from "../../api-requests/application";

/* Strict truthiness check that also strips placeholder strings the server
   sends for missing data ("N/A") and empty arrays/strings. */
const useful = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string" && (v.trim() === "" || v === "N/A")) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
};

const formatItinerary = (it) => Array.isArray(it) ? it.join(", ") : it;

const formatSmoking = (s) => {
    if (s === true || s === "true") return "Smoking allowed";
    if (s === false || s === "false") return "No smoking";
    return s;
};

const formatBool = (b) => (b === true || b === "true") ? "Yes" : "No";

/* Inline icons used in the quick-facts row */
const PinIcon = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={12} cy={10} r={3} stroke="currentColor" strokeWidth={2} />
    </svg>
);
const CalendarIcon = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
        <rect x={3} y={4} width={18} height={18} rx={2} stroke="currentColor" strokeWidth={2} />
        <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    </svg>
);
const BriefcaseIcon = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
        <rect x={2} y={7} width={20} height={14} rx={2} stroke="currentColor" strokeWidth={2} />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" stroke="currentColor" strokeWidth={2} />
    </svg>
);
const PassportIcon = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
        <path d="M4 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4z" stroke="currentColor" strokeWidth={2} />
        <circle cx={12} cy={11} r={3.5} stroke="currentColor" strokeWidth={2} />
        <path d="M9 18h6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    </svg>
);

const QuickFact = ({ icon, label, value }) => (
    <div className="jobs-accordion-quick-fact" title={label}>
        <span className="jobs-accordion-quick-fact-icon">{icon}</span>
        <span className="jobs-accordion-quick-fact-value">{value}</span>
    </div>
);

export default ({ admin, guest, withdraw, profile, expanded, handleRow, jobs, updateUserProfile, removeJob, type, setAlert, setApplications, applications }) => {

    const [applyJob, setApplyJob] = useState(null);

    if (type === "applications") jobs = jobs.map(item => { return { ...item.job, application: item } } );

    const appliedJobIds = (applications || []).map(app => String(app.job ? app.job._id : app._id));

    const refreshApplications = () => {
        return getApplications().then(apps => {
            if (setApplications) setApplications(apps);
        });
    };

    const onApply = (e, job) => {
        e.stopPropagation();
        setApplyJob(job);
    };

    const handleApplied = () => {
        refreshApplications();
    };

    const onWithdraw = (e, job) => {
        e.stopPropagation();
        const app = (applications || []).find(a => String(a.job ? a.job._id : a._id) === String(job._id));
        if (app) {
            updateApplication(app._id, [{ propName: "status", value: "cancelled" }])
                .then(() => refreshApplications());
        }
    };

    return (
        <>
        {jobs.map((job, index) => {

        let contractType = handleContractType(job);
        let startDate = handleStartDate(job);
        let hasApplied = appliedJobIds.includes(String(job._id));

        let archived = compareArchived(job.archived);
        let expired = compareExpired(job.dateArchived, job.archived);

        if (type === "applications" && expired) return <div key={index}></div>;

        const isOpen = expanded.includes(job._id);

        const location = job?.yacht?.currentLocation?.country;
        const hasOnboard = useful(job?.yacht?.smoking !== undefined ? formatSmoking(job.yacht.smoking) : null)
            || useful(job?.yacht?.tattoo)
            || (job?.yacht?.couples === true)
            || (job?.yacht?.dryBoat === true);

        return(
            <div className={`jobs-accordion-wrapper${isOpen && !guest ? " is-open" : ""}`} key={index} id={`job-${job._id}`} style={{ marginBottom: index+1 < jobs.length ? 16 : 0 }} onClick={() => handleClick(job._id, expanded, handleRow)}>
                <div className="jobs-accordion-master-content-wrapper">
                    <div className="jobs-accordion-content-wrapper">
                        <div className="jobs-accordion-content-wrapper-header-container">
                            <h2 className="jobs-accordion-title">{job.position.name}</h2>
                            <div className="jobs-accordion-header-badges">
                                {type === "applications" &&
                                    <span className={`jobs-accordion-status-badge${archived ? " is-closed" : " is-open"}`}>{archived ? "Position closed" : "Position open"}</span>
                                }
                                {hasApplied && type !== "applications" &&
                                    <span className="jobs-accordion-applied-badge">Applied</span>
                                }
                            </div>
                        </div>
                        <p className="jobs-accordion-salary">{handleJobSalary(job, true)}</p>
                        <div className="jobs-accordion-meta-row">
                            <span className="jobs-accordion-meta-item">{handleYachtSize(job.yacht.size)} {handleYachtType(job.yacht.yachtType)}</span>
                            <span className="jobs-accordion-meta-divider" aria-hidden="true">·</span>
                            <span className="jobs-accordion-contract-chip">{contractType}</span>
                            {useful(job.qualifications) && job.qualifications !== "None" && (
                                <>
                                    <span className="jobs-accordion-meta-divider" aria-hidden="true">·</span>
                                    <span className="jobs-accordion-meta-item"><span className="jobs-accordion-meta-label">Qualifications:</span> {job.qualifications}</span>
                                </>
                            )}
                        </div>
                        <p className={`jobs-accordion-description${isOpen ? " is-expanded" : ""}`}>{job.description}</p>
                        {!isOpen &&
                            <div className="jobs-accordion-footer-row">
                                {job.dateCreated && <span className="jobs-accordion-timestamp">{job.dateCreated}</span>}
                                <span className="jobs-accordion-view-link">View details <span aria-hidden="true">→</span></span>
                            </div>
                        }
                    </div>
                </div>

                {isOpen && !guest &&
                    <div className="jobs-accordion-expanded-container">

                        {/* Quick-facts — at-a-glance dealbreakers */}
                        {(useful(location) || useful(startDate) || useful(job.experience) || useful(job.visas)) &&
                            <div className="jobs-accordion-quick-facts">
                                {useful(location) && <QuickFact icon={<PinIcon />} label="Location" value={location} />}
                                {useful(startDate) && <QuickFact icon={<CalendarIcon />} label="Start date" value={startDate} />}
                                {useful(job.experience) && <QuickFact icon={<BriefcaseIcon />} label="Experience" value={job.experience} />}
                                {useful(job.visas) && <QuickFact icon={<PassportIcon />} label="Visas" value={job.visas} />}
                            </div>
                        }

                        <div className="jobs-accordion-expanded-wrapper">

                            {/* Role essentials */}
                            {(useful(contractType) || useful(job.cabinStatus) || useful(startDate) || useful(job.experience)) &&
                                <div className="jobs-accordion-expanded-wrapper-section">
                                    <p className="jobs-accordion-section-eyebrow">Role</p>
                                    <div className="jobs-accordion-section-list">
                                        {useful(contractType) && <TextRow propName={"Contract"} value={contractType} />}
                                        {useful(startDate) && <TextRow propName={"Start date"} value={startDate} />}
                                        {useful(job.cabinStatus) && <TextRow propName={"Cabin"} value={job.cabinStatus} />}
                                        {useful(job.experience) && <TextRow propName={"Experience"} value={job.experience} />}
                                        {useful(job?.consultant) && useful(job?.reference) && <TextRow propName={"Posted by"} value={`J4Crew (${job.consultant}) - Ref ${job.reference}`} />}
                                    </div>
                                </div>
                            }

                            {/* The yacht */}
                            {(useful(job?.yacht?.yachtStatus) || useful(job?.yacht?.age) || useful(job?.yacht?.yachtBuilderCountry) || useful(job?.yacht?.homePort?.country) || useful(job?.yacht?.itinerary)) &&
                                <div className="jobs-accordion-expanded-wrapper-section">
                                    <p className="jobs-accordion-section-eyebrow">The yacht</p>
                                    <div className="jobs-accordion-section-list">
                                        {useful(job?.yacht?.yachtStatus) && <TextRow propName={"Charter status"} value={job.yacht.yachtStatus} />}
                                        {useful(job?.yacht?.age) && <TextRow propName={"Year built"} value={job.yacht.age} />}
                                        {useful(job?.yacht?.yachtBuilderCountry) && <TextRow propName={"Build"} value={job.yacht.yachtBuilderCountry} />}
                                        {useful(job?.yacht?.homePort?.country) && <TextRow propName={"Home port"} value={job.yacht.homePort.country} />}
                                        {useful(job?.yacht?.itinerary) && <TextRow propName={"Itinerary"} value={formatItinerary(job.yacht.itinerary)} />}
                                    </div>
                                </div>
                            }

                            {/* Onboard - lifestyle/policy. Only render if anything notable. */}
                            {hasOnboard &&
                                <div className="jobs-accordion-expanded-wrapper-section">
                                    <p className="jobs-accordion-section-eyebrow">Onboard</p>
                                    <div className="jobs-accordion-section-list">
                                        {job?.yacht?.smoking !== undefined && <TextRow propName={"Smoking"} value={formatSmoking(job.yacht.smoking)} />}
                                        {useful(job?.yacht?.tattoo) && <TextRow propName={"Tattoos"} value={job.yacht.tattoo} />}
                                        {(job?.yacht?.couples === true || job?.yacht?.couples === "true") && <TextRow propName={"Couples"} value={"Considered"} />}
                                        {(job?.yacht?.dryBoat === true || job?.yacht?.dryBoat === "true") && <TextRow propName={"Dry boat"} value={"Yes"} />}
                                    </div>
                                </div>
                            }
                        </div>

                        {!admin && type !== "applications" &&
                            <div className="jobs-accordion-actions-row">
                                {!hasApplied ?
                                    <button type="button" className="jobs-accordion-apply-button" onClick={(e) => onApply(e, job)}>Apply for this role</button>
                                :
                                    <>
                                        <button type="button" className="jobs-accordion-withdraw-button" onClick={(e) => onWithdraw(e, job)}>Withdraw application</button>
                                        <p className="jobs-accordion-applied-confirmation">You've applied for this position.</p>
                                    </>
                                }
                            </div>
                        }
                    </div>
                }
                {isOpen && guest &&
                    <div className="jobs-accordion-guest-cta">
                        <p className="jobs-accordion-guest-cta-text">Want to see full details and apply for this role?</p>
                        <div className="jobs-accordion-guest-cta-buttons">
                            <Link to={"/sign-in"} className="jobs-accordion-guest-cta-login">Log In</Link>
                            <Link to={"/sign-up"} className="jobs-accordion-guest-cta-signup">Sign Up Free</Link>
                        </div>
                    </div>
                }
            </div>
        );
    })}
        <ApplyJobModal
            show={!!applyJob}
            job={applyJob}
            profile={profile}
            onClose={() => setApplyJob(null)}
            onApplied={handleApplied}
        />
        </>
    );

}

const handleClick = (jobId, expanded, handleRow) => {

    if (!expanded.includes(jobId)) {
        handleRow([...expanded, jobId]);
        setTimeout(() => {
            const el = document.getElementById(`job-${jobId}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "end" });
        }, 100);
        return;
    }

    return handleRow(expanded.filter(item => item !== jobId));

}
